import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { TrendingUp, TrendingDown, ShieldCheck, AlertTriangle } from 'lucide-react';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
    }).format(amount);

const getScoreColorClass = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
};

const HealthOverview = ({ data }) => {
    if (!data) return null;

    const { healthScore, risks, cashflow, emergencyFund, emiData } = data;

    return (
        <div className="space-y-4">
            {risks?.length > 0 && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Risk Alerts</AlertTitle>
                    <AlertDescription>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            {risks.map((alert, i) => (
                                <li key={i}><span className="font-semibold">{alert.type}:</span> {alert.message}</li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                {/* Health Score Card */}
                <Card className="col-span-3">
                    <CardHeader>
                        <CardTitle>Financial Health Score</CardTitle>
                        <CardDescription>Your overall financial vitality (0-100)</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center justify-center py-6">
                        <div className="relative flex items-center justify-center">
                            <svg className="h-32 w-32 transform -rotate-90">
                                <circle
                                    className="text-muted/20"
                                    strokeWidth="10"
                                    stroke="currentColor"
                                    fill="transparent"
                                    r="58"
                                    cx="64"
                                    cy="64"
                                />
                                <circle
                                    className={getScoreColorClass(healthScore?.score ?? 0)}
                                    strokeWidth="10"
                                    strokeDasharray={365}
                                    strokeDashoffset={365 - (365 * (healthScore?.score ?? 0)) / 100}
                                    strokeLinecap="round"
                                    stroke="currentColor"
                                    fill="transparent"
                                    r="58"
                                    cx="64"
                                    cy="64"
                                />
                            </svg>
                            <span className={`absolute text-4xl font-bold ${getScoreColorClass(healthScore?.score ?? 0)}`}>
                                {healthScore?.score ?? 0}
                            </span>
                        </div>
                        <p className="mt-4 text-sm text-muted-foreground text-center">Based on EMI Burden, Savings, & Cashflow</p>
                    </CardContent>
                </Card>

                {/* Key Metrics */}
                <div className="col-span-4 grid gap-4 md:grid-cols-2">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Net Cashflow</CardTitle>
                            {cashflow?.status === 'Positive' ?
                                <TrendingUp className="h-4 w-4 text-green-600" /> :
                                <TrendingDown className="h-4 w-4 text-red-600" />
                            }
                        </CardHeader>
                        <CardContent>
                            <div className={`text-2xl font-bold ${cashflow?.status === 'Positive' ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(cashflow?.netCashflow ?? 0)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                Income: {formatCurrency(cashflow?.income ?? 0)} <br />
                                Expense: {formatCurrency(cashflow?.expense ?? 0)}
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Emergency Fund</CardTitle>
                            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{emergencyFund?.coverageMonths ?? 0} Months</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                Liquid Assets: {formatCurrency(emergencyFund?.liquidAssets ?? 0)}
                            </p>
                            <Badge
                                variant={emergencyFund?.status === 'Strong' ? 'default' : 'outline'}
                                className={`mt-2 ${emergencyFund?.status === 'Strong' ? 'bg-green-600 text-white' : ''}`}
                            >
                                {emergencyFund?.status ?? 'N/A'}
                            </Badge>
                        </CardContent>
                    </Card>

                    <Card className="col-span-2">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium">EMI Burden Analysis</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>EMI Ratio</span>
                                    <span className={emiData?.riskLevel === 'Safe' ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                                        {emiData?.emiRatio ?? 0}%
                                    </span>
                                </div>
                                <Progress value={emiData?.emiRatio ?? 0} className="h-2" />
                                <div className="flex justify-between items-center mt-2">
                                    <Badge variant={emiData?.riskLevel === 'Safe' ? 'secondary' : 'destructive'}>
                                        {emiData?.riskLevel ?? 'N/A'}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">Safe Limit: 30%</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default HealthOverview;
